/**
 * AttendanceSection Component
 * Extracted from SessionDetailModal — attendance marking and viewing.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Badge,
  Loader,
  Center,
  Alert,
  Box,
  Table,
  Select,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { AlertCircle, CheckCircle, Info, Star } from 'react-feather';
import { sessionsApi } from '../../../../services/api/sessions';
import { AttendanceLoadError } from './AttendanceLoadError';
import { cohortsApi } from '../../../../services/api/cohorts';
import { getApiErrorMessage } from '../../../../services/api/client';
import type {
  SessionParticipant,
  ParticipantAttendanceInput,
  AbsentReason,
  AttendanceSubmissionInfo,
  ParticipantFlagWithDetails,
} from '../../../../types';
import type { ParticipantEngagementRating } from '../../../../services/api/coachPortal';
import {
  ENGAGEMENT_RATING_CONFIG,
  ENGAGEMENT_RATING_MAP,
} from '../../../../config/engagementRatingConfig';
import { formatDate } from '../../../../utils/formatters';
import styles from './SessionDetailModal.module.css';

const ATTENDANCE_STATUS_OPTIONS = [
  { value: 'attended', label: 'Attended' },
  { value: 'partial', label: 'Partial' },
  { value: 'absent', label: 'Absent' },
];

const ABSENT_REASON_OPTIONS = [
  { value: 'business_conflict', label: 'Business Conflict' },
  { value: 'personal_conflict', label: 'Personal Conflict' },
  { value: 'no_notice', label: 'No Notice' },
];

// Quick-select note pill presets by attendance status
const NOTE_PILLS: Record<string, string[]> = {
  partial: ['Arrived late', 'Left early'],
  absent: ['Meeting conflict', 'Work travel'],
};

function toggleNotePill(currentNotes: string, pill: string): string {
  const parts = currentNotes
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.includes(pill)) {
    return parts.filter((p) => p !== pill).join(', ');
  }
  return [...parts, pill].join(', ');
}

function isPillActive(notes: string, pill: string): boolean {
  return notes
    .split(',')
    .map((s) => s.trim())
    .includes(pill);
}

type AttendanceChoice = 'attended' | 'partial' | 'absent';

interface AttendanceRow extends SessionParticipant {
  /** MED-1360: null until the user picks, when requireExplicitStatus is on. */
  selected_status: AttendanceChoice | null;
  selected_reason: AbsentReason | null;
  notes: string;
  engagement_rating: number | null;
}

interface AttendanceSectionProps {
  cohortId: string;
  sessionId: string;
  onComplete?: () => void;
  onOpenFollowUps?: (options?: { sessionId?: string; participantId?: string }) => void;
  // MED-1078 (E1.22): on Live Session Delivery cohorts the coach/session leader is
  // the primary attendance source. The section then opens read-only with a clearly
  // secondary override path, and the legacy "CM is the primary logger" framing (an
  // editable form by default) is suppressed. False = unchanged legacy behavior (AC1).
  liveSessionDeliveryEnabled?: boolean;
  /**
   * MED-1360: a 1:1 or check-in. An unmarked participant starts with no status,
   * so a first save never records "Attended" that nobody picked. Saved
   * attendance can be cleared, back to scheduled.
   */
  oneOnOne?: boolean;
}

export function AttendanceSection({
  cohortId,
  sessionId,
  onComplete,
  onOpenFollowUps,
  liveSessionDeliveryEnabled = false,
  oneOnOne = false,
}: AttendanceSectionProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<AttendanceRow[]>([]);
  const [submissionInfo, setSubmissionInfo] = useState<AttendanceSubmissionInfo | null>(null);
  // MED-1078 (E1.22): peer-led vs coach-led role label for the LSD status line.
  const [isPeerLed, setIsPeerLed] = useState(false);

  // E19-6: Post-attendance follow-up prompt
  const [followUpPromptCount, setFollowUpPromptCount] = useState(0);
  const [showFollowUpPrompt, setShowFollowUpPrompt] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // MED-1360: true once the user changes the form, so Cancel can undo it.
  const [isTouched, setIsTouched] = useState(false);
  // MED-1360: the attendance did not load (for example a 403 on a session the
  // user does not own). The section then shows the error and no form.
  const [loadFailed, setLoadFailed] = useState(false);
  const [engagementMap, setEngagementMap] = useState<Map<string, ParticipantEngagementRating>>(
    new Map(),
  );
  const [sessionFlags, setSessionFlags] = useState<Map<string, ParticipantFlagWithDetails>>(
    new Map(),
  );
  const originalAttendance = useRef<Map<string, string>>(new Map());

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLoadFailed(false);
    try {
      const [data, feedbackData, flagsData] = await Promise.all([
        sessionsApi.getAttendance(cohortId, sessionId),
        sessionsApi.getCoachFeedback(cohortId, sessionId).catch(() => null),
        cohortsApi.getSessionFlags(cohortId, sessionId).catch(() => [] as ParticipantFlagWithDetails[]),
      ]);

      // Build engagement lookup by participant_id — one rating per participant
      const erMap = new Map<string, ParticipantEngagementRating>();
      if (feedbackData?.engagementRatings) {
        feedbackData.engagementRatings.forEach((er) => erMap.set(er.participant_id, er));
      }
      setEngagementMap(erMap);

      // Build session flag lookup by participant_id
      const flagMap = new Map<string, ParticipantFlagWithDetails>();
      flagsData.forEach((f) => flagMap.set(f.participant_id, f));
      setSessionFlags(flagMap);

      setSubmissionInfo(data.submission);
      setIsTouched(false);
      setIsPeerLed(data.is_peer_led ?? false);
      const hasExisting = data.participants.some((p) => p.current_status !== 'pending');
      // MED-1078 (E1.22) AC2/AC7: on Live Session Delivery cohorts the section
      // opens read-only by default — even when nothing has been recorded yet, the
      // CM sees the (empty) read-only state plus the status line, NOT an editable
      // form. The legacy behavior (auto-open the form when there is no existing
      // attendance) is preserved when the flag is off.
      setIsEditMode(liveSessionDeliveryEnabled ? false : !hasExisting);

      // A pending participant starts as "attended" for a group session. MED-1360:
      // a 1:1 starts with no status, so the user must pick one.
      const initialStatus = (currentStatus: string): AttendanceChoice | null =>
        currentStatus === 'pending'
          ? oneOnOne
            ? null
            : 'attended'
          : (currentStatus as AttendanceChoice);

      // Store original attendance for change tracking
      // Use the MAPPED status so it matches initial selected_status
      const origMap = new Map<string, string>();
      data.participants.forEach((p) => {
        origMap.set(p.user_id, initialStatus(p.current_status) ?? '');
      });
      originalAttendance.current = origMap;

      setParticipants(
        data.participants.map((p) => ({
          ...p,
          selected_status: initialStatus(p.current_status),
          selected_reason: p.current_absent_reason,
          notes: p.current_notes || '',
          engagement_rating: erMap.get(p.user_id)?.rating ?? null,
        })),
      );
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load attendance data'));
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [cohortId, sessionId, liveSessionDeliveryEnabled, oneOnOne]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateParticipant = (
    userId: string,
    field: 'selected_status' | 'selected_reason' | 'notes' | 'engagement_rating',
    value: string | null,
  ) => {
    setIsTouched(true);
    setParticipants((prev) =>
      prev.map((p) => {
        if (p.user_id !== userId) return p;
        if (field === 'selected_status') {
          const newStatus = value as 'attended' | 'partial' | 'absent';
          return {
            ...p,
            selected_status: newStatus,
            selected_reason: newStatus === 'absent' ? p.selected_reason : null,
            // Clear engagement rating when marking absent
            engagement_rating: newStatus === 'absent' ? null : p.engagement_rating,
          };
        }
        if (field === 'selected_reason') {
          return { ...p, selected_reason: value as AbsentReason | null };
        }
        if (field === 'notes') {
          return { ...p, notes: value || '' };
        }
        if (field === 'engagement_rating') {
          return { ...p, engagement_rating: value ? parseInt(value, 10) : null };
        }
        return p;
      }),
    );
  };

  // Compute changed participants
  const changedParticipants = useMemo(() => {
    return participants.filter(
      (p) => (p.selected_status ?? '') !== originalAttendance.current.get(p.user_id),
    );
  }, [participants]);

  const changedToAttendedOrPartial = useMemo(() => {
    return changedParticipants.filter(
      (p) => p.selected_status === 'attended' || p.selected_status === 'partial',
    );
  }, [changedParticipants]);

  const missingStatus = participants.some((p) => p.selected_status === null);

  const handleSave = async (sendSurveys: boolean) => {
    if (missingStatus) {
      setError('Choose a status for every participant.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const attendance: ParticipantAttendanceInput[] = participants.map((p) => ({
        user_id: p.user_id,
        status: p.selected_status as AttendanceChoice,
        absent_reason: p.selected_status === 'absent' ? p.selected_reason : null,
        notes: p.notes || null,
      }));

      // Build engagement ratings payload (only include participants with a rating set)
      const engagementRatings = participants
        .filter((p) => p.engagement_rating !== null && p.selected_status !== 'absent')
        .map((p) => ({ participant_id: p.user_id, rating: p.engagement_rating! }));

      if (!submissionInfo) {
        await sessionsApi.markAttendance(cohortId, sessionId, {
          attendance,
          send_surveys: sendSurveys,
          engagement_ratings: engagementRatings,
        });
      } else {
        const changedUserIds = changedToAttendedOrPartial.map((p) => p.user_id);
        await sessionsApi.updateAttendance(cohortId, sessionId, attendance, {
          send_surveys: sendSurveys,
          changed_user_ids: changedUserIds,
          engagement_ratings: engagementRatings,
        });
      }

      // E19-6: Count absent participants for follow-up prompt
      const absentCount = attendance.filter(a => a.status === 'absent').length;
      if (absentCount > 0 && onOpenFollowUps) {
        setFollowUpPromptCount(absentCount);
        setShowFollowUpPrompt(true);
      }

      setIsEditMode(false);
      await loadData();
      onComplete?.();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save attendance'));
    } finally {
      setSaving(false);
    }
  };

  // MED-1360: undo a wrong mark. The session keeps its date; a past one then
  // shows as needing attendance, so the text does not say "scheduled".
  const handleClear = () => {
    modals.openConfirmModal({
      title: 'Clear attendance',
      children: (
        <Text size="sm">
          This removes the attendance mark. The session stays on its date, and you can mark it
          again later.
        </Text>
      ),
      labels: { confirm: 'Clear attendance', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setSaving(true);
        setError(null);
        try {
          await sessionsApi.clearAttendance(cohortId, sessionId);
          await loadData();
          onComplete?.();
        } catch (err) {
          setError(getApiErrorMessage(err, 'Failed to clear attendance'));
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // MED-1360: Cancel undoes the edit when there is one. Saved attendance or an
  // edit opened from the read-only view goes back to that view. A first mark
  // with no saved attendance resets the picks. loadData sets the mode for both.
  // Cancel waits while a save runs. A reload that ends after the save would
  // bring back the old data, and the next save would mark instead of update.
  const canCancelEdit = !saving && (!!submissionInfo || liveSessionDeliveryEnabled || isTouched);
  const handleCancelEdit = () => {
    loadData();
  };

  const summary = {
    total: participants.length,
    attended: participants.filter((p) => p.selected_status === 'attended').length,
    partial: participants.filter((p) => p.selected_status === 'partial').length,
    absent: participants.filter((p) => p.selected_status === 'absent').length,
  };
  const percentage =
    summary.total > 0
      ? Math.round(((summary.attended + summary.partial) / summary.total) * 100)
      : 0;

  const isViewOnly = !isEditMode;

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  // MED-1360: no attendance loaded, so there is nothing to edit or clear. An
  // empty form here had Save and Save & Send Surveys enabled.
  if (loadFailed) {
    return <AttendanceLoadError message={error} onRetry={loadData} />;
  }

  // MED-1078 (E1.22) AC3/AC4a: state-aware status line for Live Session Delivery
  // cohorts. Role copy follows the modality (coach for coach-led, session leader
  // for peer-led). The line distinguishes (1) not-yet-submitted, (2) submitted by
  // the facilitator vs recorded-first by the CM, and (3) edited-after-submission —
  // surfacing the original submission date plus the latest editor + date.
  const roleLabel = isPeerLed ? 'Session leader' : 'Coach';
  const roleLabelLower = isPeerLed ? 'session leader' : 'coach';

  const renderLsdStatusLine = () => {
    if (!submissionInfo) {
      // AC3 — not yet submitted; CM may still record it now (AC7).
      return (
        <Alert icon={<Info size={16} />} color="gray" variant="light">
          <Text size="sm" fw={500}>
            Pending {roleLabelLower} submission
          </Text>
          <Text size="xs" c="dimmed">
            You can record attendance now if needed.
          </Text>
        </Alert>
      );
    }
    const originalDate = formatDate(submissionInfo.submitted_at);
    // AC3 — facilitator submitted vs CM recorded first (coach/leader never confirmed).
    const baseText = submissionInfo.submitted_by_facilitator
      ? `${roleLabel} submitted attendance on ${originalDate}`
      : `Recorded by ${submissionInfo.submitted_by_name} on ${originalDate}`;
    // AC3/AC4a — once edited after submission, append the latest editor + date.
    const editedSuffix =
      submissionInfo.last_edited_at && submissionInfo.last_edited_by_name
        ? ` · last edited by ${submissionInfo.last_edited_by_name} on ${formatDate(
            submissionInfo.last_edited_at,
          )}`
        : '';
    return (
      <Alert icon={<CheckCircle size={16} />} color="green" variant="light">
        <Text size="sm">
          {baseText}
          {editedSuffix}
        </Text>
      </Alert>
    );
  };

  return (
    <Stack gap="md">
      {liveSessionDeliveryEnabled ? (
        renderLsdStatusLine()
      ) : (
        submissionInfo && (
          <Alert icon={<CheckCircle size={16} />} color="green" variant="light">
            <Text size="sm">
              Attendance submitted by {submissionInfo.submitted_by_name} on{' '}
              {formatDate(submissionInfo.submitted_at)}
            </Text>
          </Alert>
        )
      )}

      {/* E19-6: Post-attendance follow-up prompt */}
      {showFollowUpPrompt && onOpenFollowUps && (
        <Alert
          icon={<Info size={16} />}
          color="warningAmber"
          variant="light"
          withCloseButton
          onClose={() => setShowFollowUpPrompt(false)}
        >
          <Group justify="space-between" align="center">
            <Text size="sm">
              {followUpPromptCount} participant{followUpPromptCount !== 1 ? 's were' : ' was'} absent this session.
            </Text>
            <Group gap="xs">
              <Button
                size="xs"
                variant="filled"
                color="medleyGreen"
                onClick={() => {
                  onOpenFollowUps({ sessionId });
                  setShowFollowUpPrompt(false);
                }}
              >
                Review Follow Ups
              </Button>
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                onClick={() => setShowFollowUpPrompt(false)}
              >
                Dismiss
              </Button>
            </Group>
          </Group>
        </Alert>
      )}

      {error && (
        <Alert icon={<AlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      {/* MED-1078 (E1.22): on an LSD cohort with nothing recorded yet, the counts
          would be meaningless (pending maps to "attended" for editing) — hide the
          summary so the read-only state does not imply everyone attended. */}
      {!(liveSessionDeliveryEnabled && !submissionInfo) && (
        <Box
          p="sm"
          style={{
            backgroundColor: 'var(--mantine-color-gray-0)',
            borderRadius: 'var(--mantine-radius-sm)',
          }}
        >
          <Group justify="space-between">
            <Group gap="md">
              <Badge color="green" variant="light">
                {summary.attended} Attended
              </Badge>
              <Badge color="yellow" variant="light">
                {summary.partial} Partial
              </Badge>
              <Badge color="red" variant="light">
                {summary.absent} Absent
              </Badge>
            </Group>
            <Text fw={600}>{percentage}% Attendance</Text>
          </Group>
        </Box>
      )}

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Participant</Table.Th>
            <Table.Th>Status</Table.Th>
            {isViewOnly && <Table.Th>Engagement</Table.Th>}
            <Table.Th>Reason</Table.Th>
            {!isViewOnly && <Table.Th>Engagement</Table.Th>}
            <Table.Th>Notes</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {participants.map((p) => {
            const flag = sessionFlags.get(p.user_id);
            const isWithdrawn = p.participant_status === 'withdrawn';
            return (
            <Table.Tr
              key={p.user_id}
              style={{
                ...(flag && !isWithdrawn ? { backgroundColor: 'rgba(233, 162, 59, 0.08)' } : {}),
                ...(isWithdrawn ? { opacity: 0.5 } : {}),
              }}
            >
              <Table.Td>
                <Stack gap={0}>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      {p.first_name} {p.last_name}
                    </Text>
                    {isWithdrawn && (
                      <Badge
                        size="xs"
                        variant="filled"
                        styles={{
                          root: {
                            backgroundColor: '#E8E8E8',
                            color: '#444444',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                            fontWeight: 600,
                          },
                        }}
                      >
                        {p.withdrawn_at ? `WITHDRAWN ${new Date(p.withdrawn_at).getMonth() + 1}/${new Date(p.withdrawn_at).getDate()}` : 'WITHDRAWN'}
                      </Badge>
                    )}
                    {!isWithdrawn && flag && (
                      <Badge
                        size="xs"
                        color="warningAmber"
                        variant="filled"
                        title={flag.flag_reason}
                      >
                        FLAGGED
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed">
                    {p.email}
                  </Text>
                </Stack>
              </Table.Td>
              <Table.Td>
                {isViewOnly ? (
                  // MED-1078 (E1.22): in the LSD read-only view a still-pending
                  // participant has no recorded status yet — show "Not recorded"
                  // rather than defaulting the badge to "Attended" (the edit-mode
                  // mapping). Legacy cohorts keep their existing rendering.
                  (liveSessionDeliveryEnabled && p.current_status === 'pending') ||
                  p.selected_status === null ? (
                    <Text size="sm" c="dimmed">
                      Not recorded
                    </Text>
                  ) : (
                    <Badge
                      color={
                        p.selected_status === 'attended'
                          ? 'green'
                          : p.selected_status === 'partial'
                            ? 'yellow'
                            : 'red'
                      }
                      variant="light"
                    >
                      {p.selected_status.charAt(0).toUpperCase() + p.selected_status.slice(1)}
                    </Badge>
                  )
                ) : (
                  <Select
                    size="xs"
                    data={ATTENDANCE_STATUS_OPTIONS}
                    value={p.selected_status}
                    placeholder="Choose"
                    // MED-1325: Mantine clears the value when the selected option is
                    // clicked again. A cleared status was saved as null and failed.
                    allowDeselect={false}
                    onChange={(value) => updateParticipant(p.user_id, 'selected_status', value)}
                    style={{ width: 120 }}
                  />
                )}
              </Table.Td>
              {isViewOnly && (
                <Table.Td>
                  {(() => {
                    const er = engagementMap.get(p.user_id);
                    if (!er) return <Text size="sm" c="dimmed">—</Text>;
                    const config = ENGAGEMENT_RATING_MAP.get(er.rating);
                    return (
                      <Group gap={6}>
                        <Group gap={2}>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              size={12}
                              fill={star <= er.rating ? 'var(--mantine-color-yellow-5)' : 'none'}
                              color={
                                star <= er.rating
                                  ? 'var(--mantine-color-yellow-5)'
                                  : 'var(--mantine-color-gray-4)'
                              }
                            />
                          ))}
                        </Group>
                        <Badge size="xs" color={config?.color || 'gray'} variant="light">
                          {config?.label || `${er.rating}/5`}
                        </Badge>
                      </Group>
                    );
                  })()}
                </Table.Td>
              )}
              <Table.Td>
                {p.selected_status === 'absent' ? (
                  isViewOnly ? (
                    <Text size="sm">
                      {ABSENT_REASON_OPTIONS.find((o) => o.value === p.selected_reason)?.label ||
                        '—'}
                    </Text>
                  ) : (
                    <Select
                      size="xs"
                      data={ABSENT_REASON_OPTIONS}
                      value={p.selected_reason || ''}
                      onChange={(value) =>
                        updateParticipant(p.user_id, 'selected_reason', value)
                      }
                      placeholder="Select reason"
                      clearable
                      style={{ width: 150 }}
                    />
                  )
                ) : (
                  <Text size="sm" c="dimmed">
                    —
                  </Text>
                )}
              </Table.Td>
              {!isViewOnly && (
                <Table.Td>
                  {p.selected_status === 'absent' ? (
                    <Text size="sm" c="dimmed">—</Text>
                  ) : (
                    <Group gap={4} wrap="nowrap">
                      {ENGAGEMENT_RATING_CONFIG.map((config) => {
                        const isSelected = p.engagement_rating === config.value;
                        const ratingInfo = ENGAGEMENT_RATING_MAP.get(config.value);
                        return (
                          <Tooltip
                            key={config.value}
                            label={ratingInfo?.label || ''}
                            position="top"
                            withArrow
                          >
                            <UnstyledButton
                              onClick={() =>
                                updateParticipant(
                                  p.user_id,
                                  'engagement_rating',
                                  isSelected ? null : String(config.value),
                                )
                              }
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 'var(--mantine-radius-sm)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                fontSize: 12,
                                border: isSelected
                                  ? `2px solid var(--mantine-color-${config.color}-6)`
                                  : '1px solid var(--mantine-color-gray-3)',
                                backgroundColor: isSelected
                                  ? `var(--mantine-color-${config.color}-1)`
                                  : 'var(--mantine-color-white)',
                                color: isSelected
                                  ? `var(--mantine-color-${config.color}-8)`
                                  : 'var(--mantine-color-gray-6)',
                                transition: 'all 150ms ease',
                              }}
                            >
                              {config.value}
                            </UnstyledButton>
                          </Tooltip>
                        );
                      })}
                    </Group>
                  )}
                </Table.Td>
              )}
              <Table.Td>
                {isViewOnly ? (
                  <Text size="sm">{p.notes || '—'}</Text>
                ) : (
                  <Stack gap={4}>
                    {p.selected_status && (NOTE_PILLS[p.selected_status] || []).length > 0 && (
                      <Group gap={4}>
                        {NOTE_PILLS[p.selected_status].map((pill) => (
                          <Badge
                            key={pill}
                            size="xs"
                            variant={isPillActive(p.notes, pill) ? 'filled' : 'light'}
                            color="gray"
                            style={{ cursor: 'pointer', textTransform: 'none' }}
                            onClick={() =>
                              updateParticipant(
                                p.user_id,
                                'notes',
                                toggleNotePill(p.notes, pill),
                              )
                            }
                          >
                            {pill}
                          </Badge>
                        ))}
                      </Group>
                    )}
                    <Textarea
                      size="xs"
                      value={p.notes}
                      onChange={(e) =>
                        updateParticipant(p.user_id, 'notes', e.target.value)
                      }
                      placeholder="Optional notes"
                      rows={1}
                      style={{ width: 200 }}
                    />
                  </Stack>
                )}
              </Table.Td>
            </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      {/* Change tracking callout */}
      {!isViewOnly && submissionInfo && changedParticipants.length > 0 && (
        <Alert
          icon={<Info size={16} />}
          color="blue"
          variant="light"
          className={styles.changeCallout}
        >
          <Text size="sm">
            Attendance updated for:{' '}
            {changedParticipants.map((p) => `${p.first_name} ${p.last_name}`).join(', ')}.
            {changedToAttendedOrPartial.length > 0 &&
              ' These participants will receive survey emails if you choose "Save & Send Surveys".'}
          </Text>
        </Alert>
      )}

      {/* Footer buttons */}
      <Group justify="space-between">
        {isViewOnly ? (
          <>
            {oneOnOne && submissionInfo ? (
              <Button variant="subtle" color="red" onClick={handleClear} loading={saving}>
                Clear attendance
              </Button>
            ) : (
              <div />
            )}
            {/* MED-1078 (E1.22) AC4: on LSD cohorts the affordance is framed as a
                correction/override of the coach/leader-owned record — "Override"
                once attendance exists, "Record attendance" when nothing is recorded
                yet. Legacy cohorts keep "Edit Attendance". */}
            <Button variant="light" onClick={() => setIsEditMode(true)}>
              {liveSessionDeliveryEnabled
                ? submissionInfo
                  ? 'Override'
                  : 'Record attendance'
                : 'Edit Attendance'}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="subtle"
              color="gray"
              onClick={handleCancelEdit}
              disabled={!canCancelEdit}
            >
              Cancel
            </Button>
            <Group gap="sm">
              {liveSessionDeliveryEnabled && submissionInfo ? (
                // MED-1078 (E1.22) AC6: an override saves silently by default —
                // surveys are NOT re-sent. The silent "Save" is the primary
                // (filled) action; "Save & Send Surveys" remains available as an
                // explicit, clearly-secondary choice, and only when there is
                // actually someone newly marked attended/partial to survey.
                changedToAttendedOrPartial.length > 0 ? (
                  <>
                    <Button
                      variant="subtle"
                      color="gray"
                      onClick={() => handleSave(true)}
                      loading={saving}
                      disabled={missingStatus}
                    >
                      Save & Send Surveys
                    </Button>
                    <Button onClick={() => handleSave(false)} loading={saving} disabled={missingStatus}>
                      Save
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => handleSave(false)} loading={saving} disabled={missingStatus}>
                    Save
                  </Button>
                )
              ) : submissionInfo && changedToAttendedOrPartial.length > 0 ? (
                <>
                  <Button variant="outline" onClick={() => handleSave(false)} loading={saving} disabled={missingStatus}>
                    Save Only
                  </Button>
                  <Button onClick={() => handleSave(true)} loading={saving} disabled={missingStatus}>
                    Save & Send Surveys
                  </Button>
                </>
              ) : !submissionInfo ? (
                <>
                  <Button variant="outline" onClick={() => handleSave(false)} loading={saving} disabled={missingStatus}>
                    Save Only
                  </Button>
                  <Button onClick={() => handleSave(true)} loading={saving} disabled={missingStatus}>
                    Save & Send Surveys
                  </Button>
                </>
              ) : (
                <Button onClick={() => handleSave(false)} loading={saving} disabled={missingStatus}>
                  Save Changes
                </Button>
              )}
            </Group>
          </>
        )}
      </Group>
    </Stack>
  );
}

export default AttendanceSection;
